import { dateUtils } from '@festina-life/core'
import { Tooltip } from '@festina-life/ui/feedback'

import { useUntilChangesetKeyValue } from '../../context/useUntilChangesetKeyValue'
import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import type { TemplateVersionInfo } from '../../types/templateModel'
import { NO_DATE_SYMBOL } from '../../util/constants'
import { useOperationsContext } from '../../workspace/operationsContext'
import { ARROW_LINE_WIDTH } from '../versionedTimeline/constants'
import { OperationVersionCircle } from '../versionedTimeline/OperationVersionCircle'
import type { useTimelineScale } from '../versionedTimeline/useTimelineScale'
import { TemplateVersionTooltipContent } from './TemplateVersionTooltipContent'
import { useIsTemplateVersionActive } from './useIsTemplateVersionActive'

type Props = {
  version: TemplateVersionInfo
  dateToPixel: ReturnType<typeof useTimelineScale>['dateToPixel']
  isInherited: (version: TemplateVersionInfo) => boolean
  currentVersionLineId: string
}

export const SingleTemplateVersionCircle = ({
  version,
  dateToPixel,
  isInherited,
  currentVersionLineId,
}: Props) => {
  const { workspaceKey, branchKey } = useOperationsContext()
  const { server } = useNavigationParams()
  const checkIsVersionActive = useIsTemplateVersionActive(currentVersionLineId)
  const untilChangesetKeyValue = useUntilChangesetKeyValue()
  const isLockedToChangeset =
    untilChangesetKeyValue === version.changesetHistory?.changesetKey
  const isActive = checkIsVersionActive(version)
  const cxPosition = dateToPixel(
    version.fromDate
      ? dateUtils.dateUtcAsLocal(version.fromDate).toDate()
      : NO_DATE_SYMBOL,
  )

  return (
    <Tooltip
      tip={
        <TemplateVersionTooltipContent
          versions={[version]}
          versionId={currentVersionLineId}
        />
      }
      isInteractive
    >
      <g>
        <OperationVersionCircle
          isActive={isActive}
          isDraft={version.isDraft}
          isInherited={isInherited(version)}
          isHideVersion={version.isHideVersion}
          cy={ARROW_LINE_WIDTH / 2}
          cx={cxPosition}
          isLockedToChangeset={isLockedToChangeset}
          to={
            !version.isDraft
              ? configurationItem({
                  layer: version.layer,
                  block: version.block,
                  itemId: version.identifier,
                  type: 'template',
                  workspaceName: workspaceKey?.name,
                  branch: branchKey?.name,
                  server: workspaceKey?.branchKey.server ?? server,
                  untilChangesetKey: version.changesetHistory?.changesetKey,
                })
              : undefined
          }
          isBeginningOfTime={!version.fromDate}
        />
      </g>
    </Tooltip>
  )
}
